import * as React from 'react';
import { Storage, handleFlagChange, AsyncStorage } from 'services';
import { useState, useEffect } from 'react';
import { BootScreen } from 'screens';

// Types
type Action =
  | { type: 'TOGGLE_GROUPS'; payload: boolean }
  | { type: 'UPDATE_FLAGS'; payload: string };
type Dispatch = (action: Action) => void;
type State = {
  groupsFeatureFlag: boolean;
};

const FeatureFlagStateContext = React.createContext<State | undefined>(undefined);

const FeatureFlagDispatchContext = React.createContext<Dispatch | undefined>(undefined);

async function getInitialState() {
  try {
    const groupsFeatureFlag = (await AsyncStorage.get('ff-groups')) ? true : false;
    return {
      groupsFeatureFlag,
    } as State;
  } catch (error) {
    const defaultState = {
      groupsFeatureFlag: false,
    };
    return defaultState;
  }
}

export const featureFlagReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'TOGGLE_GROUPS':
      if (action.payload !== state.groupsFeatureFlag) {
        if (action.payload === true) {
          Storage.set('ff-groups', action.payload);
        } else {
          Storage.remove('ff-groups');
        }
        handleFlagChange();
      }
      return {
        ...state,
        groupsFeatureFlag: action.payload,
      };
    default:
      return state;
  }
};

export const FeatureFlagProvider: React.ComponentType<React.ReactNode> = ({ children }: any) => {
  const [state, setState] = useState<any>(null);

  useEffect(() => {
    getInitialState().then((response) => {
      setState(response);
    });
  }, []);

  if (!state) return <BootScreen />;

  return <Provider initialState={state}>{children}</Provider>;
};

const Provider = ({ children, initialState }: any) => {
  const [state, dispatch] = React.useReducer(featureFlagReducer, initialState);
  return (
    <FeatureFlagStateContext.Provider value={state}>
      <FeatureFlagDispatchContext.Provider value={dispatch}>
        {children}
      </FeatureFlagDispatchContext.Provider>
    </FeatureFlagStateContext.Provider>
  );
};

export const useFeatureFlagState = () => {
  const context = React.useContext(FeatureFlagStateContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagState must be used within a FeatureFlagProvider');
  }
  return context;
};

export const useFeatureFlagDispatch = () => {
  const context = React.useContext(FeatureFlagDispatchContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagDispatch must be used within a FeatureFlagProvider');
  }
  return context;
};
